import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Surface } from "./Primitives";
import { Warning, X, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const SEV_STYLE = {
  expired:     { label: "EXPIRED", border: "border-red-500/60",    bg: "bg-red-500/10",    text: "text-red-300",    pill: "bg-red-500/30 text-red-200" },
  urgent_7d:   { label: "URGENT",  border: "border-orange-500/60", bg: "bg-orange-500/10", text: "text-orange-300", pill: "bg-orange-500/30 text-orange-200" },
  warning_30d: { label: "30 DAYS", border: "border-amber/60",      bg: "bg-amber/10",      text: "text-amber",      pill: "bg-amber/20 text-amber" },
};

function SeverityCounts({ counts }) {
  const parts = [];
  if (counts.expired) parts.push(`${counts.expired} expired`);
  if (counts.urgent_7d) parts.push(`${counts.urgent_7d} urgent`);
  if (counts.warning_30d) parts.push(`${counts.warning_30d} 30-day`);
  if (!parts.length) return null;
  return (
    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
      {parts.join(" · ")}
    </span>
  );
}

function AlertRow({ alert }) {
  const s = SEV_STYLE[alert.severity] || SEV_STYLE.warning_30d;
  const due = alert.due_in_days <= 0
    ? `${Math.abs(alert.due_in_days)}d ago`
    : `in ${alert.due_in_days}d`;
  return (
    <div className="flex items-center gap-3 py-2.5 flex-wrap"
         data-testid={`alert-row-${alert.kind}-${alert.severity}`}>
      <span className={`tag font-mono text-[10px] tracking-wider ${s.pill}`} style={{ padding: "2px 8px" }}>
        {s.label}
      </span>
      <div className="text-sm text-zinc-200 flex-1 min-w-0 truncate">{alert.message}</div>
      <div className="font-mono text-xs text-zinc-500 whitespace-nowrap">
        {due} · {alert.due_date}
      </div>
    </div>
  );
}

function BannerHeader({ counts, scope, includeAll, isOwner, onToggleAll, onDismiss, dismissable, topStyle }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3 min-w-0">
        <Warning size={22} className={`${topStyle.text} flex-shrink-0 mt-0.5`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-base font-medium ${topStyle.text}`}>Compliance attention required</h3>
            <SeverityCounts counts={counts} />
          </div>
          <div className="text-xs text-zinc-400 mt-1 font-mono">
            {scope === "me" && !includeAll
              ? "Items concerning your medical, contract, recency or aircraft you fly."
              : "All compliance items across the group."}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {isOwner && scope === "me" && (
          <button
            type="button"
            onClick={onToggleAll}
            className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber font-mono border border-zinc-700 px-2 py-1"
            data-testid="alerts-toggle-all"
          >
            {includeAll ? "Show: mine" : "Show: all participants"}
          </button>
        )}
        {dismissable && (
          <button onClick={onDismiss} className="text-zinc-500 hover:text-zinc-200" data-testid="alerts-dismiss">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export function AlertsBanner({ scope = "me", maxRows = 4, dismissable = true }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [includeAll, setIncludeAll] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(() => {
    const q = includeAll && isOwner ? "all" : scope;
    api.get(`/alerts/expiring?scope=${q}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [scope, includeAll, isOwner]);

  useEffect(() => { load(); }, [load]);

  if (dismissed || items.length === 0) return null;

  const visible = showAll ? items : items.slice(0, maxRows);
  const counts = items.reduce((acc, a) => {
    acc[a.severity] = (acc[a.severity] || 0) + 1;
    return acc;
  }, {});
  const topStyle = SEV_STYLE[items[0]?.severity] || SEV_STYLE.warning_30d;

  return (
    <Surface className={`border ${topStyle.border} ${topStyle.bg} p-5`} data-testid="alerts-banner">
      <BannerHeader
        counts={counts}
        scope={scope}
        includeAll={includeAll}
        isOwner={isOwner}
        onToggleAll={() => setIncludeAll((v) => !v)}
        onDismiss={() => setDismissed(true)}
        dismissable={dismissable}
        topStyle={topStyle}
      />
      <div className="mt-4 border-t border-zinc-800/60 divide-y divide-zinc-800/60">
        {visible.map((a, i) => (
          <AlertRow key={`${a.kind}-${a.user_id || a.aircraft_id || i}-${a.due_date}`} alert={a} />
        ))}
      </div>
      {items.length > maxRows && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber font-mono inline-flex items-center gap-1"
          data-testid="alerts-toggle-expand"
        >
          {showAll ? "Show less" : `Show all ${items.length}`}
          <ArrowSquareOut size={11} />
        </button>
      )}
    </Surface>
  );
}

export default AlertsBanner;
